import { useRef, useState } from 'react'
import { Package, CheckCircle, FileUser, Camera, Loader2, ImageIcon } from 'lucide-react'
import { VendorBadge } from './VendorBadge'
import { notify } from '@/shared/utils'
import type { Vendor, InventoryItem } from '@/shared/types'

interface ProfileHeaderProps {
  vendor: Vendor | null
  inventory?: InventoryItem[]
  onCoverImageUpload?: (file: File) => Promise<string | void>
  isUploadingCover?: boolean
}

export function ProfileHeader({ 
  vendor, 
  inventory = [], 
  onCoverImageUpload,
  isUploadingCover = false 
}: ProfileHeaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const totalItems = inventory.length
  const availableItems =
    vendor?.type === 'FOOD'
      ? inventory.filter((item) => 'isAvailable' in item && item.isAvailable).length
      : inventory.filter((item) => 'variants' in item && item.variants?.some((v) => v.stockQty > 0)).length

  const handleCoverClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify.error('Image must be less than 5MB')
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify.error('Please select an image file')
      return
    }

    // Show preview immediately
    const preview = URL.createObjectURL(file)
    setPreviewUrl(preview)

    try {
      if (onCoverImageUpload) {
        await onCoverImageUpload(file)
        notify.success('Cover image updated!')
      }
    } catch {
      notify.error('Failed to upload cover image')
      setPreviewUrl(null)
    }

    // Reset input
    e.target.value = ''
  }

  const coverImage = previewUrl || vendor?.coverImage

  return (
    <div className="border rounded-xl overflow-hidden mb-6 bg-card">
      {/* Cover Image Section */}
      <div 
        className="relative h-32 sm:h-40 bg-gradient-to-br from-primary/20 via-primary/10 to-accent/20 cursor-pointer group"
        onClick={handleCoverClick}
      >
        {coverImage ? (
          <img 
            src={coverImage} 
            alt="Store cover" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Click to add cover image</p>
            </div>
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {isUploadingCover ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : (
            <div className="text-center text-white">
              <Camera className="w-8 h-8 mx-auto mb-1" />
              <span className="text-sm font-medium">
                {coverImage ? 'Change cover' : 'Add cover'}
              </span>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          disabled={isUploadingCover}
        />
      </div>

      {/* Profile Info Section */}
      <div className="p-6 sm:p-8 -mt-10 sm:-mt-12 relative">
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6">
          {/* Profile Image - Overlapping the cover */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl border-4 border-card shadow-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            {vendor?.profileImage ? (
              <img src={vendor.profileImage} alt={vendor?.name} className="w-full h-full object-cover" />
            ) : (
              <FileUser className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground" />
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center sm:text-left pt-2 sm:pt-0">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-4 mb-2">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground">{vendor?.name || 'Vendor'}</h2>
              {vendor?.type && <VendorBadge type={vendor.type} />}
            </div>
            {vendor?.isVerified && (
              <p className="text-xs text-primary flex items-center gap-1 justify-center sm:justify-start">
                <CheckCircle className="w-3 h-3" />
                Verified Vendor
              </p>
            )}
          </div>

          {/* Quick Stats */}
          <div className="flex gap-6 sm:gap-8">
            <div className="text-center">
              <div className="flex items-center gap-1.5 text-foreground justify-center">
                <Package className="w-4 h-4 text-muted-foreground" />
                <span className="font-bold text-lg">{totalItems}</span>
              </div>
              <p className="text-xs text-muted-foreground">Total Items</p>
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1.5 text-primary justify-center">
                <CheckCircle className="w-4 h-4" />
                <span className="font-bold text-lg">{availableItems}</span>
              </div>
              <p className="text-xs text-muted-foreground">Available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
